import { filterConstants } from "../../constants";
import {
  useQuickSubmitFormHelper,
  useEditItemProperties,
  useGetPageQueryParams,
  useMerchantApiQueryWithParams,
  useQueryString,
  useReactRouterLink,
} from "../../hooks";
import {
  IQueryStringNavLinks,
  ITableHeader,
  IViewPaymentLink,
  IPageAndSearchRequest,
  PagedMeta,
} from "../../interfaces-and-types";
import { QuickSubmitForm } from "../../components";

import { View } from "./View";
import { paymentLinkStatus } from "../../enums";

interface IPaymentLinkQueryMeta {
  status: string;
  start_date: string | null;
  end_date: string | null;
}

export interface IPaymentLinkViewProps {
  data: IViewPaymentLink[];
  headers: ITableHeader[];
  isLoading: boolean;
  isError: boolean;
  totalCount: number;
  queryStringTabs: IQueryStringNavLinks;

  // modals
  refreshTable: () => void;
  openDetailsModal?: (data: any) => void;
  openCreateNewModal: () => void;

  // extra buttons
  onExport: () => void;
}

export const PaymentLinks = () => {
  const query = useQueryString();
  const status = query.get(filterConstants.statusKey);
  const { page, page_size, search } = useGetPageQueryParams();

  const startDate = query.get(filterConstants.startDateKey);
  const endDate = query.get(filterConstants.endDateKey);

  const { navigateToRoutePreserveParams } = useReactRouterLink();

  const {
    isLoading: isLoadingPaymentLinks,
    isError: isErrorPaymentLinks,
    // isSuccess: isSuccessPaymentLinks,
    data: paymentLinks,
    meta: paymentLinksMeta,
    refetch: refetchPaymentLinks,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & IPaymentLinkQueryMeta,
    IViewPaymentLink[],
    PagedMeta
  >([
    "getPaymentLinks",
    {
      page: (page && Number(page)) || 1,
      page_size: (page_size && Number(page_size)) || 10,
      search,
      status: status || "active",
      start_date: startDate,
      end_date: endDate,
    },
  ]);

  const headers: ITableHeader[] = [
    {
      key: "link_url",
      header: "Link Url",
    },
    {
      key: "status",
      header: "Status",
    },
    {
      key: "products",
      header: "Name/Products",
    },
    {
      key: "amount",
      header: "Amount",
    },
    {
      key: "date",
      header: "Date Created",
    },
    {
      key: "more",
      header: "",
    },
  ];

  const queryStringTabs: IQueryStringNavLinks = {
    query: filterConstants.statusKey,
    options: [
      {
        key: "Available",
        value: "active",
      },
      {
        key: "Archived",
        value: "deactivated",
      },
    ],
  };

  const onExport = () => {
    alert("exporting data...");
  };

  const refreshTable = () => refetchPaymentLinks();

  // const refreshTable = () => {
  //   queryClient.invalidateQueries([
  //     "getPaymentLinks",
  //     {
  //       page: (page && Number(page)) || 1,
  //       page_size: (page_size && Number(page_size)) || 10,
  //       search,
  //       status: status || "active",
  //     },
  //   ]);
  // };

  // insert the functions to open edit/delete modals
  const paymentLinksWithRoutes: IViewPaymentLink[] | undefined =
    paymentLinks?.map((pl) => ({
      ...pl,

      // add routes
      openEditModal: () => navigateToRoutePreserveParams(`${pl.id}/edit`),
      openDeleteModal: () => openDeleteModal(pl.id, pl.is_delete_locked),
      archivePaymentLink: () =>
        openArchiveModal(
          pl.id,
          {
            ...pl,
            status:
              pl.status === paymentLinkStatus.active
                ? paymentLinkStatus.deactivated
                : paymentLinkStatus.active,
          },
          false /*pl.isEditLocked*/,
          pl.status === paymentLinkStatus.deactivated
        ),
    }));

  const openDetailsModal = (id: any) =>
    navigateToRoutePreserveParams(`${id}/details`);

  const openCreateNewModal = () => {
    navigateToRoutePreserveParams(`create`);
  };

  const {
    isModalOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,

    isItemDeleteLocked,

    isLoading: isLoadingDeleteItem,
    isSuccess: isSuccessDeleteItem,
    mutate: deleteItem,
  } = useQuickSubmitFormHelper("deletePaymentLink", refreshTable);

  const {
    isEditModalOpen: isArchiveModalOpen,
    openEditModal: openArchiveModal,
    closeEditModal: closeArchiveModal,

    isItemEditLocked,

    isLoadingEditItem: isLoadingArchiveItem,
    isSuccessEditItem: isSuccessArchiveItem,
    editItem: archiveItem,
  } = useEditItemProperties("editPaymentLink", refreshTable);

  return (
    <>
      <View
        data={paymentLinksWithRoutes || []}
        headers={headers}
        isLoading={isLoadingPaymentLinks}
        isError={isErrorPaymentLinks}
        totalCount={paymentLinksMeta?.page_meta.item_count || 0}
        queryStringTabs={queryStringTabs}
        onExport={onExport}
        refreshTable={refreshTable}
        openCreateNewModal={openCreateNewModal}
        openDetailsModal={openDetailsModal}
      />

      <QuickSubmitForm
        formTitle="Delete payment link"
        isModalOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
        isDeleteLocked={isItemDeleteLocked}
        isSubmitting={isLoadingDeleteItem}
        isSuccess={isSuccessDeleteItem}
        onButtonClick={deleteItem}
        confirmationMessage={
          <>
            <h4>Are you sure you want to delete this payment link?</h4>
            <br />

            <span className="text-danger">
              (NOTE: This action is irreversible)
            </span>
          </>
        }
      />

      <QuickSubmitForm
        formTitle="Archive Payment Link"
        buttonText="Archive"
        buttonKind="secondary"
        isModalOpen={isArchiveModalOpen}
        closeModal={closeArchiveModal}
        isDeleteLocked={isItemEditLocked}
        isSubmitting={isLoadingArchiveItem}
        isSuccess={isSuccessArchiveItem}
        onButtonClick={() => archiveItem()}
        confirmationMessage={
          <>
            <h4>Are you sure you want to archive this payment link?</h4>
            <br />

            <span className="text-danger">
              (NOTE: You may restore it at any time)
            </span>
          </>
        }
      />
    </>
  );
};
